// pages/SharedDashboard.jsx — Halaman PUBLIK read-only untuk share link.
// URL: /share/:key (tanpa token di URL). Token divalidasi sekali di
// /api/share/:key/open yang menaruh sesi pada cookie HttpOnly, lalu redirect
// ke sini. Data diambil dari endpoint share (/api/share/:key/data) yang
// divalidasi via cookie (credentials:'include'), lalu di-render memakai
// komponen dashboard yang sama (AwsCostDashboard) dalam mode dataLoader.
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import AwsCostDashboard from '../dashboards/AwsCostDashboard';
import AWSDashboard from '../dashboards/AWSDashboard';
import SolmanDashboard from '../dashboards/SolmanDashboard';
import M365Dashboard from './M365Dashboard';
import EmployeeOvertime from './EmployeeOvertime';

const API = (typeof window !== 'undefined' && window.__AWS_API_BASE__) || '';

// Key yang didukung di mode share → komponen dashboard-nya.
// Tambah entri di sini saat membuka dashboard lain untuk di-share.
const SHARE_VIEWS = {
  'aws-cost':          AwsCostDashboard,
  'aws-billing':       AWSDashboard,
  'solman':            SolmanDashboard,
  'm365':              M365Dashboard,
  'employee-overtime': EmployeeOvertime,
};

// ── Markdown renderer (sama dengan di Notes.jsx, di-inline agar tidak dep) ──
function mdInline(text, keyPrefix) {
  const nodes = []; let rest = text; let i = 0;
  const re = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(_[^_]+_)|(\[[^\]]+\]\([^)]+\))/;
  while (rest.length) {
    const m = rest.match(re);
    if (!m) { nodes.push(rest); break; }
    if (m.index > 0) nodes.push(rest.slice(0, m.index));
    const tok = m[0]; const k = `${keyPrefix}-${i++}`;
    if (tok.startsWith('`')) nodes.push(<code key={k} style={{ background:'#f1f5f9',padding:'1px 5px',borderRadius:4,fontFamily:'monospace',fontSize:'0.92em',color:'#be123c' }}>{tok.slice(1,-1)}</code>);
    else if (tok.startsWith('**')) nodes.push(<strong key={k} style={{ fontWeight:700,color:'#0f172a' }}>{tok.slice(2,-2)}</strong>);
    else if (tok.startsWith('*') || tok.startsWith('_')) nodes.push(<em key={k}>{tok.slice(1,-1)}</em>);
    else { const lm = tok.match(/\[([^\]]+)\]\(([^)]+)\)/); if (lm) nodes.push(<a key={k} href={lm[2]} target="_blank" rel="noopener noreferrer" style={{ color:'rgb(var(--brand-600))',textDecoration:'underline' }}>{lm[1]}</a>); else nodes.push(tok); }
    rest = rest.slice(m.index + tok.length);
  }
  return nodes;
}

function MarkdownView({ source }) {
  const lines = String(source || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = []; let i = 0; let bi = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (/^```/.test(line.trim())) {
      const buf = []; i++;
      while (i < lines.length && !/^```/.test(lines[i].trim())) { buf.push(lines[i]); i++; }
      i++;
      blocks.push(<pre key={`b${bi++}`} style={{ background:'#0f172a',color:'#e2e8f0',borderRadius:8,padding:'12px 14px',fontSize:12.5,fontFamily:'monospace',overflowX:'auto',lineHeight:1.5,margin:'8px 0' }}>{buf.join('\n')}</pre>);
      continue;
    }
    if (/^(-{3,}|\*{3,}|_{3,})\s*$/.test(line.trim())) { blocks.push(<hr key={`b${bi++}`} style={{ border:0,borderTop:'1px solid #e2e8f0',margin:'14px 0' }} />); i++; continue; }
    const hm = line.match(/^(#{1,6})\s+(.*)$/);
    if (hm) {
      const level = hm[1].length; const sizes = {1:20,2:17,3:15,4:14,5:13,6:12.5}; const Tag = `h${Math.min(level,6)}`;
      blocks.push(<Tag key={`b${bi++}`} style={{ fontSize:sizes[level]||14,fontWeight:700,color:'#0f172a',margin:level<=2?'16px 0 8px':'12px 0 6px',lineHeight:1.3 }}>{mdInline(hm[2],`h${bi}`)}</Tag>);
      i++; continue;
    }
    if (/^>\s?/.test(line)) {
      const buf = []; while (i < lines.length && /^>\s?/.test(lines[i])) { buf.push(lines[i].replace(/^>\s?/,'')); i++; }
      blocks.push(<blockquote key={`b${bi++}`} style={{ borderLeft:'3px solid #cbd5e1',paddingLeft:12,margin:'8px 0',color:'#475569',fontStyle:'italic' }}>{buf.map((b,j)=><div key={j}>{mdInline(b,`bq${bi}-${j}`)}</div>)}</blockquote>);
      continue;
    }
    if (/^\s*[-*+]\s+/.test(line)) {
      const items = []; while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*[-*+]\s+/,'')); i++; }
      blocks.push(<ul key={`b${bi++}`} style={{ margin:'8px 0',paddingLeft:22,lineHeight:1.6 }}>{items.map((it,j)=><li key={j} style={{ marginBottom:3 }}>{mdInline(it,`ul${bi}-${j}`)}</li>)}</ul>);
      continue;
    }
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = []; while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*\d+\.\s+/,'')); i++; }
      blocks.push(<ol key={`b${bi++}`} style={{ margin:'8px 0',paddingLeft:24,lineHeight:1.6 }}>{items.map((it,j)=><li key={j} style={{ marginBottom:3 }}>{mdInline(it,`ol${bi}-${j}`)}</li>)}</ol>);
      continue;
    }
    if (line.trim() === '') { i++; continue; }
    const isDivider = (s) => /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*\|?\s*$/.test(s) && s.includes('-');
    if (line.includes('|') && i+1 < lines.length && isDivider(lines[i+1])) {
      const splitRow = (s) => s.trim().replace(/^\|/,'').replace(/\|$/,'').split('|').map(c=>c.trim());
      const headers = splitRow(line);
      const aligns = splitRow(lines[i+1]).map(d => { const t=d.trim(); if(t.startsWith(':')&&t.endsWith(':'))return'center'; if(t.endsWith(':'))return'right'; return'left'; });
      i += 2; const rows = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '' && !isDivider(lines[i])) { rows.push(splitRow(lines[i])); i++; }
      blocks.push(<div key={`b${bi++}`} style={{ overflowX:'auto',margin:'10px 0' }}><table style={{ borderCollapse:'collapse',width:'100%',fontSize:12.5,border:'1px solid #e2e8f0' }}><thead><tr style={{ background:'#f8fafc' }}>{headers.map((h,ci)=><th key={ci} style={{ textAlign:aligns[ci]||'left',padding:'7px 12px',fontWeight:700,color:'#0f172a',borderBottom:'2px solid #e2e8f0',whiteSpace:'nowrap' }}>{mdInline(h,`th${bi}-${ci}`)}</th>)}</tr></thead><tbody>{rows.map((r,ri)=><tr key={ri} style={{ background:ri%2?'#f8fafc':'#fff' }}>{headers.map((_,ci)=><td key={ci} style={{ textAlign:aligns[ci]||'left',padding:'6px 12px',color:'#334155',borderBottom:'1px solid #f1f5f9' }}>{mdInline((r[ci]||''),`td${bi}-${ri}-${ci}`)}</td>)}</tr>)}</tbody></table></div>);
      continue;
    }
    const para = [];
    while (i < lines.length && lines[i].trim() !== '' && !/^```/.test(lines[i].trim()) && !/^#{1,6}\s/.test(lines[i]) && !/^>\s?/.test(lines[i]) && !/^\s*[-*+]\s+/.test(lines[i]) && !/^\s*\d+\.\s+/.test(lines[i]) && !/^(-{3,}|\*{3,}|_{3,})\s*$/.test(lines[i].trim())) { para.push(lines[i]); i++; }
    blocks.push(<p key={`b${bi++}`} style={{ margin:'6px 0',lineHeight:1.6,color:'#334155',wordBreak:'break-word' }}>{para.map((pl,j)=><span key={j}>{mdInline(pl,`p${bi}-${j}`)}{j<para.length-1?<br/>:null}</span>)}</p>);
  }
  return <div style={{ fontSize:13 }}>{blocks}</div>;
}

// ── NoteView — render satu note dalam mode publik ────────────────────────────
function NoteView({ noteKey, id }) {
  const {
    t
  } = useLang();

  const [state, setState] = useState('loading');
  const [note,  setNote]  = useState(null);
  const [err,   setErr]   = useState('');
  const [tab,   setTab]   = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res  = await fetch(`${API}/api/share/${encodeURIComponent(noteKey)}/data`, { credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        if (!alive) return;
        setNote(data); setState('ready');
      } catch (e) {
        if (!alive) return;
        setErr(e.message || (t('shared.failedLoadNote')));
        setState('error');
      }
    })();
    return () => { alive = false; };
  }, [noteKey, id]);

  if (state === 'loading') return (
    <div style={{ padding:'3rem', textAlign:'center', color:'#94a3b8' }}>
      <div style={{ width:28,height:28,border:'3px solid rgb(var(--brand-600))',borderTopColor:'transparent',borderRadius:'50%',animation:'shspin 0.8s linear infinite',margin:'0 auto 12px' }} />
      {t('shared.loadingNote')}
    </div>
  );
  if (state === 'error') return (
    <div style={{ padding:'3rem', textAlign:'center', color:'#dc2626', fontSize:14 }}>{err}</div>
  );
  if (!note) return null;

  const tabs    = Array.isArray(note.tabs) ? note.tabs : [];
  const hasTabs = tabs.length > 0;
  const active  = Math.min(tab, tabs.length - 1);
  // Konten yang ditampilkan: tab aktif bila ada tab, selain itu kolom content.
  const body    = hasTabs ? (tabs[active]?.content || '') : (note.content || '');

  return (
    <div style={{ maxWidth:780, margin:'0 auto', padding:'2rem 1.5rem' }}>
      <h1 style={{ fontSize:24, fontWeight:700, color:'#0f172a', marginBottom:8, lineHeight:1.3 }}>
        {note.title || (t('shared.untitled'))}
      </h1>
      <div style={{ fontSize:12, color:'#94a3b8', marginBottom: hasTabs ? 16 : 24, paddingBottom:16, borderBottom:'1px solid #f1f5f9' }}>
        {note.updatedBy ? `${t('shared.by')} ${note.updatedBy} · ` : ''}{note.updatedAt || ''}
      </div>
      {hasTabs && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:20 }}>
          {tabs.map((t, ti) => (
            <button
              key={ti}
              onClick={() => setTab(ti)}
              style={{
                padding:'5px 12px', borderRadius:8, cursor:'pointer',
                fontSize:12.5, fontWeight:600, lineHeight:1.2,
                border: ti === active ? '1px solid rgb(var(--brand-600))' : '1px solid #e2e8f0',
                background: ti === active ? 'rgb(var(--brand-50))' : '#fff',
                color: ti === active ? 'rgb(var(--brand-600))' : '#475569',
                maxWidth:240, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
              }}
              title={t.title || ''}
            >
              {t.title || (id ? `Tab ${ti + 1}` : `Tab ${ti + 1}`)}
            </button>
          ))}
        </div>
      )}
      {body.trim()
        ? <MarkdownView source={body} />
        : <div style={{ color:'#94a3b8', fontSize:13, fontStyle:'italic' }}>{t('shared.noContent')}</div>}
    </div>
  );
}

// ── AiWorkspaceView — chat publik ramping: General + common Notes RAG ────────
function AiWorkspaceView({ id }) {
  const {
    t
  } = useLang();
  const [mode,   setMode]   = useState('__general__');            // '__general__' | 'notes'
  const [msgs,   setMsgs]   = useState([]);            // { role:'user'|'assistant', content }
  const [input,  setInput]  = useState('');
  const [sid,    setSid]    = useState(null);
  const [busy,   setBusy]   = useState(false);

  const [err,    setErr]    = useState('');

  const send = async () => {
    const q = input.trim();
    if (!q || busy) return;
    setErr(''); setBusy(true);
    setMsgs(m => [...m, { role: 'user', content: q }]);
    setInput('');
    try {
      const res = await fetch(`${API}/api/share/ai-workspace/ai`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: mode, question: q, session_id: sid, lang: id ? 'id' : 'en' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      if (data.session_id) setSid(data.session_id);
      setMsgs(m => [...m, { role: 'assistant', content: data.answer || '' }]);
    } catch (e) {
      setErr(e.message || t('shared.failedGetAnswer'));
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };
  const switchMode = (m) => { if (m === mode) return; setMode(m); setMsgs([]); setSid(null); setErr(''); };

  const ModeBtn = ({ value, label }) => (
    <button
      onClick={() => switchMode(value)}
      style={{
        padding:'5px 14px', borderRadius:999, cursor:'pointer', fontSize:12.5, fontWeight:700,
        border: mode === value ? '1px solid rgb(var(--brand-600))' : '1px solid #e2e8f0',
        background: mode === value ? 'rgb(var(--brand-50))' : '#fff',
        color: mode === value ? 'rgb(var(--brand-600))' : '#64748b',
      }}
    >{label}</button>
  );

  return (
    <div style={{ maxWidth:820, margin:'0 auto', padding:'1.5rem 1rem', display:'flex', flexDirection:'column', minHeight:'calc(100vh - 56px)' }}>
      <div style={{ display:'flex', gap:8, marginBottom:16, alignItems:'center' }}>
        <ModeBtn value="__general__" label={t('shared.general')} />
        <ModeBtn value="notes" label={t('shared.notesCommon')} />
      </div>
      <div style={{ flex:1, overflowY:'auto', display:'flex', flexDirection:'column', gap:12, paddingBottom:12 }}>
        {msgs.length === 0 && (
          <div style={{ color:'#94a3b8', fontSize:13, textAlign:'center', margin:'2rem 0' }}>
            {mode === 'notes'
              ? t('shared.askAnythingAboutTeamS')
              : t('shared.startGeneralConversationAi')}
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth:'85%' }}>
            <div style={{
              background: m.role === 'user' ? 'rgb(var(--brand-600))' : '#fff',
              color: m.role === 'user' ? '#fff' : '#0f172a',
              border: m.role === 'user' ? 'none' : '1px solid #e2e8f0',
              borderRadius:12, padding:'10px 14px', fontSize:13.5, lineHeight:1.5,
              boxShadow:'0 1px 2px rgba(0,0,0,0.04)', wordBreak:'break-word',
            }}>
              {m.role === 'user' ? m.content : <MarkdownView source={m.content} />}
            </div>
          </div>
        ))}
        {busy && (
          <div style={{ alignSelf:'flex-start', color:'#94a3b8', fontSize:13, padding:'6px 4px' }}>
            {t('shared.aiTyping')}
          </div>
        )}
      </div>
      {err && (
        <div style={{ background:'#fff1f2', color:'#dc2626', border:'1px solid #fecdd3', borderRadius:8, padding:'8px 12px', fontSize:12.5, marginBottom:8 }}>
          {err}
        </div>
      )}
      <div style={{ display:'flex', gap:8, alignItems:'flex-end', borderTop:'1px solid #e2e8f0', paddingTop:12 }}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          rows={1}
          placeholder={t('shared.typeQuestion')}
          style={{ flex:1, resize:'none', border:'1px solid #cbd5e1', borderRadius:10, padding:'10px 12px', fontSize:13.5, fontFamily:'inherit', outline:'none', maxHeight:140 }}
        />
        <button
          onClick={send}
          disabled={busy || !input.trim()}
          style={{
            padding:'10px 18px', borderRadius:10, border:'none', fontSize:13.5, fontWeight:700,
            cursor: busy || !input.trim() ? 'not-allowed' : 'pointer',
            background: busy || !input.trim() ? '#cbd5e1' : 'rgb(var(--brand-600))', color:'#fff',
          }}
        >{t('shared.send')}</button>
      </div>
    </div>
  );
}

// ── Chart renderer (format blok \`\`\`chart JSON — sama dgn AIChat/MarkdownRenderer) ─
const CHART_COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2', '#db2777', '#65a30d', '#ea580c', '#475569'];
function ShareChart({ spec }) {
  if (!spec || !Array.isArray(spec.data) || spec.data.length === 0) return null;
  const type = String(spec.type || 'bar').toLowerCase();
  const data = spec.data.slice(0, 40);
  const xKey = spec.xKey || 'name';
  // Kunci numerik: dari "series" bila ada, selain itu semua field number ≠ xKey.
  const seriesKeys = Array.isArray(spec.series) && spec.series.length
    ? spec.series.map(s => (typeof s === 'string' ? s : s.key)).filter(Boolean)
    : Object.keys(data[0] || {}).filter(k => k !== xKey && typeof data[0][k] === 'number');
  const H = 260;
  const numFmt = (v) => (typeof v === 'number' ? v.toLocaleString('en-US') : v);

  let chart = null;
  if (type === 'pie' || type === 'donut') {
    chart = (
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%"
             innerRadius={type === 'donut' ? 55 : 0} outerRadius={90}
             label={(e) => e.name} labelLine={false}>
          {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
        </Pie>
        <Tooltip formatter={numFmt} /><Legend />
      </PieChart>
    );
  } else if (type === 'radar') {
    chart = (
      <RadarChart data={data}>
        <PolarGrid /><PolarAngleAxis dataKey={xKey} tick={{ fontSize: 11 }} />
        {seriesKeys.map((k, i) => <Radar key={k} name={k} dataKey={k} stroke={CHART_COLORS[i % CHART_COLORS.length]} fill={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.35} />)}
        <Tooltip formatter={numFmt} /><Legend />
      </RadarChart>
    );
  } else if (type === 'line') {
    chart = (
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} tickFormatter={numFmt} />
        {seriesKeys.map((k, i) => <Line key={k} type="monotone" dataKey={k} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={false} />)}
        <Tooltip formatter={numFmt} /><Legend />
      </LineChart>
    );
  } else if (type === 'area') {
    chart = (
      <AreaChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} tickFormatter={numFmt} />
        {seriesKeys.map((k, i) => <Area key={k} type="monotone" dataKey={k} stroke={CHART_COLORS[i % CHART_COLORS.length]} fill={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.25} />)}
        <Tooltip formatter={numFmt} /><Legend />
      </AreaChart>
    );
  } else {
    // bar / barh / stacked_bar / composed → BarChart (barh = layout vertical)
    const horizontal = type === 'barh';
    const stacked = type === 'stacked_bar';
    chart = (
      <BarChart data={data} layout={horizontal ? 'vertical' : 'horizontal'}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        {horizontal
          ? <><XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={numFmt} /><YAxis type="category" dataKey={xKey} width={130} tick={{ fontSize: 11 }} /></>
          : <><XAxis dataKey={xKey} tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} tickFormatter={numFmt} /></>}
        {seriesKeys.map((k, i) => <Bar key={k} dataKey={k} stackId={stacked ? 'a' : undefined} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={horizontal ? [0,4,4,0] : [4,4,0,0]} />)}
        <Tooltip formatter={numFmt} /><Legend />
      </BarChart>
    );
  }

  return (
    <div style={{ margin:'10px 0', border:'1px solid #e2e8f0', borderRadius:10, padding:'12px 8px 4px', background:'#fff' }}>
      {spec.title && <div style={{ fontWeight:700, fontSize:12.5, color:'#0f172a', margin:'0 8px 6px' }}>{spec.title}{spec.unit ? <span style={{ color:'#94a3b8', fontWeight:500 }}> ({spec.unit})</span> : null}</div>}
      <ResponsiveContainer width="100%" height={H}>{chart}</ResponsiveContainer>
    </div>
  );
}

// Pecah jawaban assistant → segmen markdown & chart (blok \`\`\`chart JSON).
function splitChartBlocks(text) {
  const parts = [];
  const re = /```chart\s*\n([\s\S]*?)```/g;
  let last = 0, m;
  while ((m = re.exec(String(text || ''))) !== null) {
    if (m.index > last) parts.push({ kind: 'md', text: text.slice(last, m.index) });
    let spec = null;
    try { spec = JSON.parse(m[1]); } catch (_) { /* JSON rusak → tampilkan sbg kode */ }
    parts.push(spec ? { kind: 'chart', spec } : { kind: 'md', text: m[0] });
    last = m.index + m[0].length;
  }
  if (last < String(text || '').length) parts.push({ kind: 'md', text: text.slice(last) });
  return parts.length ? parts : [{ kind: 'md', text: String(text || '') }];
}

// Efek ketik (pola AIChat): reveal teks polos per-tick; klik = skip; markdown/
// tabel/chart penuh dirender SEKALI saat selesai (hindari dobel-render).
const TW_MS = 16, TW_TICKS = 90, TW_MIN = 40;
function safeTypedPreview(text, n) {
  let s = String(text || '').slice(0, n);
  const fences = (s.match(/```/g) || []).length;
  if (fences % 2 === 1) { const i = s.lastIndexOf('```'); if (i >= 0) s = s.slice(0, i); }
  return s;
}
function useTypewriter(fullText, animate, onDone) {
  const should = animate && String(fullText || '').length >= TW_MIN;
  const [len, setLen] = useState(should ? 0 : String(fullText || '').length);
  const doneRef = useRef(!should);
  const onDoneRef = useRef(onDone);
  useEffect(() => { onDoneRef.current = onDone; });
  useEffect(() => {
    if (doneRef.current) return;
    const L = String(fullText || '').length;
    const step = Math.max(2, Math.ceil(L / TW_TICKS));
    const id = setInterval(() => {
      setLen(prev => {
        const next = Math.min(L, prev + step);
        if (next >= L) { clearInterval(id); doneRef.current = true; onDoneRef.current && onDoneRef.current(); }
        return next;
      });
    }, TW_MS);
    return () => clearInterval(id);
  }, [fullText]);
  const finish = () => { doneRef.current = true; setLen(String(fullText || '').length); onDoneRef.current && onDoneRef.current(); };
  return { typing: should && len < String(fullText || '').length, len, finish };
}

// Satu bubble assistant: typewriter → lalu markdown + chart + tabel penuh.
function AssistantBubble({ msg, id, onDoneAnimate }) {
  const {
    t
  } = useLang();

  const { typing, len, finish } = useTypewriter(msg.content, !!msg._animate, onDoneAnimate);
  return (
    <div onClick={typing ? finish : undefined} title={typing ? (t('shared.clickShowFull')) : undefined}
      style={{ background:'#f8fafc', color:'#0f172a', border:'1px solid #e2e8f0', borderRadius:12,
               padding:'9px 12px', fontSize:13, lineHeight:1.5, wordBreak:'break-word', cursor: typing ? 'pointer' : 'default' }}>
      {typing
        ? <span style={{ whiteSpace:'pre-wrap' }}>{safeTypedPreview(msg.content, len)}<span style={{ opacity:0.5 }}>▌</span></span>
        : <>
            {splitChartBlocks(msg.content).map((p, i) =>
              p.kind === 'chart' ? <ShareChart key={i} spec={p.spec} /> : <MarkdownView key={i} source={p.text} />)}
            {msg.rows && <MiniTable rows={msg.rows} />}
          </>}
    </div>
  );
}

// Tabel ringan untuk hasil query (right-align numerik + format ribuan/mata uang).
function MiniTable({ rows }) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const cols = Object.keys(rows[0]);
  const isNum = {}, isCur = {};
  for (const c of cols) {
    const lc = c.toLowerCase();
    isNum[c] = rows.every(r => r[c] == null || r[c] === '' || !isNaN(Number(String(r[c]).replace(/,/g, ''))));
    isCur[c] = /amount/.test(lc) ? 'idr' : (/usd|cost/.test(lc) ? 'usd' : null);
  }
  const fmt = (v, c) => {
    if (v == null || v === '') return '';
    if (!isNum[c]) return String(v);
    const n = Number(String(v).replace(/,/g, ''));
    if (!isFinite(n)) return String(v);
    if (isCur[c] === 'idr') return 'Rp ' + Math.round(n).toLocaleString('id-ID');
    if (isCur[c] === 'usd') return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return Number.isInteger(n) ? n.toLocaleString('en-US') : n.toFixed(2);
  };
  return (
    <div style={{ overflowX:'auto', margin:'8px 0', border:'1px solid #e2e8f0', borderRadius:8 }}>
      <table style={{ borderCollapse:'collapse', width:'100%', fontSize:12.5 }}>
        <thead>
          <tr style={{ background:'#f8fafc' }}>
            {cols.map(c => <th key={c} style={{ textAlign:isNum[c]?'right':'left', padding:'6px 10px', fontWeight:700, color:'#0f172a', borderBottom:'1px solid #e2e8f0', whiteSpace:'nowrap' }}>{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} style={{ background: ri % 2 ? '#f8fafc' : '#fff' }}>
              {cols.map(c => <td key={c} style={{ textAlign:isNum[c]?'right':'left', padding:'6px 10px', color:'#334155', borderBottom:'1px solid #f1f5f9', whiteSpace:'nowrap' }}>{fmt(r[c], c)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── ShareSqlAiPanel — chat text-to-SQL pada dashboard share (source dipaksa server) ─
// [MR-PG-AI] Quick questions untuk link share chat-only. Pertanyaan yang sama
// dengan AI Workspace (sudah diuji & dicocokkan ke report MR, 2026-10-16).
const SHARE_QUICK_PROMPTS = {
  'mr-maintenance': {
    id: ['Berapa total biaya maintenance bulan ini dibanding budgetnya?',
         'Bandingkan aktual vs budget maintenance per bulan selama 2026, tampilkan grafiknya',
         'Bulan apa saja yang biaya maintenance-nya melebihi budget?',
         'Departemen mana yang paling boros biaya maintenance tahun ini?',
         'Plant mana yang biaya maintenance-nya paling tinggi?'],
    en: ['What is the total maintenance cost this month versus budget?',
         'Compare actual vs budget maintenance cost per month in 2026, show a chart',
         'Which months had maintenance cost above budget?',
         'Which department has the highest maintenance cost this year?',
         'Which plant has the highest maintenance cost?'],
  },
  'mr-inventory': {
    id: ['Berapa total nilai stok saat ini per company?',
         'Tampilkan 10 material dengan nilai stok terbesar saat ini',
         'Berapa nilai stok slow moving dan dead stock per kategori?',
         'Material apa yang paling banyak dipakai (konsumsi) bulan lalu?'],
    en: ['What is the total current stock value per company?',
         'Show the 10 materials with the highest current stock value',
         'What is the slow moving and dead stock value per category?',
         'Which materials were consumed the most last month?'],
  },
};

function ShareSqlAiPanel({ shareKey, id, standalone }) {
  const {
    t
  } = useLang();
  const [open,  setOpen]  = useState(!!standalone);   // standalone (chat-only) = selalu terbuka
  const [max,   setMax]   = useState(false);      // maximize / fullscreen
  const [msgs,  setMsgs]  = useState([]);      // { role, content, rows?, usage?, model?, ms? }
  const [input, setInput] = useState('');
  const [sid,   setSid]   = useState(null);
  const [busy,  setBusy]  = useState(false);
  const [err,   setErr]   = useState('');
  const [editIdx,  setEditIdx]  = useState(-1);   // indeks pesan user yg di-edit
  const [editText, setEditText] = useState('');
  // Floating drag/resize (hanya berlaku saat non-standalone & non-max). null =
  // pakai posisi/ukuran default (kanan-bawah). Setelah user geser/resize, nilai
  // eksplisit dipakai. Disimpan di ref+state: ref utk baca cepat saat drag.
  const [floatBox, setFloatBox] = useState(null);   // { left, top, width, height }
  const panelRef = useRef(null);

  // ── [SHARE HISTORY] Sidebar riwayat — HANYA saat panel di-maximize ──────────
  // Riwayat dikunci per DASHBOARD, bukan per pengunjung: semua pemegang link
  // melihat daftar sesi yang sama (keputusan produk — lihat catatan di share.js).
  //
  // Sidebar sengaja dibatasi ke mode maximize. Panel floating lebarnya ~460px
  // dan bisa diperkecil sampai 320px; menyelipkan kolom daftar di situ membuat
  // gelembung chat tinggal ~180px dan tabel hasil tidak terbaca sama sekali.
  const [sessions,    setSessions]    = useState([]);
  const [sessBusy,    setSessBusy]    = useState(false);
  const [sessErr,     setSessErr]     = useState('');
  const [loadingSid,  setLoadingSid]  = useState(null);   // sesi yg sedang dibuka
  const [sessQ,       setSessQ]       = useState('');     // teks yang diketik
  const [sessQDeb,    setSessQDeb]    = useState('');     // versi ter-debounce
  const [sessLimit,   setSessLimit]   = useState(null);   // batas dari server
  const [renameId,    setRenameId]    = useState(null);   // sesi yg sedang diganti judul
  const [renameText,  setRenameText]  = useState('');
  const [renameBusy,  setRenameBusy]  = useState(false);
  const TITLE_MAX = 120;   // sama dengan batas normalisasi di server

  const startRename = (s, e) => {
    e.stopPropagation();
    setRenameId(s.session_id);
    setRenameText(s.title || '');
  };
  const cancelRename = () => { setRenameId(null); setRenameText(''); };

  const commitRename = async (sessionId, e) => {
    e?.stopPropagation?.();
    if (renameBusy) return;
    setRenameBusy(true);
    try {
      const res = await fetch(
        `${API}/api/share/${encodeURIComponent(shareKey)}/ai/session/${encodeURIComponent(sessionId)}/title`,
        { method:'PUT', credentials:'include',
          headers:{ 'Content-Type':'application/json' },
          body: JSON.stringify({ title: renameText }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      // Judul kosong = kembali ke judul otomatis, dan server yang menentukan
      // bentuk akhirnya. Karena itu daftar disegarkan alih-alih menambal state
      // lokal dengan teks yang diketik — kalau tidak, judul otomatis hasil
      // penghapusan tidak akan pernah muncul.
      cancelRename();
      await loadSessions(sessQDeb);
    } catch (err) {
      setErr(err.message || 'error');
    } finally { setRenameBusy(false); }
  };

  // Debounce 300ms: tiap ketikan memicu query LIKE lintas seluruh pesan
  // dashboard ini. Tanpa jeda, mengetik 20 huruf = 20 pemindaian tabel.
  useEffect(() => {
    const h = setTimeout(() => setSessQDeb(sessQ.trim()), 300);
    return () => clearTimeout(h);
  }, [sessQ]);

  const loadSessions = useCallback(async (q = '') => {
    setSessBusy(true); setSessErr('');
    try {
      const qs  = q ? `?q=${encodeURIComponent(q)}` : '';
      const res = await fetch(`${API}/api/share/${encodeURIComponent(shareKey)}/ai/sessions${qs}`, {
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setSessions(Array.isArray(data.sessions) ? data.sessions : []);
      // Editor rename dibatalkan tiap daftar dimuat ulang: barisnya bisa hilang
      // atau bergeser, dan editor yang menggantung akan menyimpan judul ke sesi
      // yang salah.
      setRenameId(null);
      // Batas datang DARI SERVER (AI_SHARE_SESSIONS_LIMIT), tidak ditulis ulang
      // di sini. Angka yang diduplikasi di dua sisi pasti menyimpang begitu
      // env-nya diubah, dan keterangannya jadi berbohong tanpa gejala.
      if (Number.isFinite(data.limit)) setSessLimit(data.limit);
    } catch (e) {
      setSessErr(e.message || 'error');
      setSessions([]);
    } finally { setSessBusy(false); }
  }, [shareKey]);

  // Muat tiap kali masuk mode maximize ATAU kata kunci berubah — bukan sekali
  // seumur mount. Sesi baru (milik pengunjung ini maupun pengunjung lain) harus
  // ikut terlihat tanpa perlu reload halaman.
  // [MR-PG-AI] chat-only (standalone) memenuhi halaman → sidebar riwayat selalu tampil.
  const showHistory = max || !!standalone;
  useEffect(() => { if (showHistory) loadSessions(sessQDeb); }, [showHistory, sessQDeb, loadSessions]);

  // Buka satu sesi: transkripnya dimuat DAN `sid` ikut diset, sehingga pesan
  // berikutnya menyambung ke sesi yang sama (bukan memulai sesi baru diam-diam).
  const openSession = async (sessionId) => {
    if (busy || loadingSid) return;
    setLoadingSid(sessionId); setErr('');
    try {
      const res = await fetch(
        `${API}/api/share/${encodeURIComponent(shareKey)}/ai/session/${encodeURIComponent(sessionId)}`,
        { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setMsgs((Array.isArray(data.messages) ? data.messages : []).map(x => ({
        role: x.role === 'assistant' ? 'assistant' : 'user',
        content: x.content || '',
        rows: Array.isArray(x.rows) ? x.rows : null,
        sql: x.sql || null,
        usage: x.usage || null,
        provider: x.provider || null,
        model: x.model || null,
        // _animate sengaja TIDAK diset: animasi ketik hanya untuk jawaban baru.
      })));
      setSid(sessionId);
      setEditIdx(-1); setInput('');
    } catch (e) {
      setErr(e.message || 'error');
    } finally { setLoadingSid(null); }
  };

  // ── [DEEP LINK] ?session=<id> — "Open in new tab" dari daftar riwayat ───────
  // Panel dipaksa TERBUKA sekaligus MAXIMIZE. Alasannya: tab hasil hanya berisi
  // satu transkrip, jadi panel floating kecil di pojok kanan-bawah menyisakan
  // 90% layar untuk dashboard yang bukan alasan tab itu dibuka. Maximize juga
  // satu-satunya mode yang memunculkan sidebar riwayat, sehingga sesi lain
  // langsung terjangkau dari tab yang sama.
  //
  // Dibaca dari window.location: halaman share tidak melakukan navigasi router
  // yang mengubah query, jadi tidak perlu berlangganan perubahan. Ref guard
  // mencegah pemuatan ulang bila komponen ter-render ulang.
  const deepLinkDoneRef = useRef(false);
  useEffect(() => {
    if (deepLinkDoneRef.current) return;
    const sid = new URLSearchParams(window.location.search).get('session') || '';
    if (!sid) return;
    deepLinkDoneRef.current = true;
    setOpen(true);
    // standalone (chat-only) sudah memenuhi seluruh halaman dan JUSTRU rusak
    // bila di-maximize — mode itu dirender inline, bukan lewat portal.
    if (!standalone) setMax(true);
    openSession(sid);
  }, []); // eslint-disable-line

  // Chat baru: sid dikosongkan agar server membuat sesi baru pada pesan pertama.
  const newChat = () => {
    if (busy) return;
    setMsgs([]); setSid(null); setErr(''); setInput(''); setEditIdx(-1);
  };

  // Statistik sesi: total token, rata-rata waktu, model terakhir.
  const stat = msgs.reduce((a, m) => {
    if (m.role !== 'assistant') return a;
    const u = m.usage || {};
    a.p += u.prompt_tokens || 0; a.c += u.completion_tokens || 0; a.tot += u.total_tokens || 0;
    if (m.ms) { a.msSum += m.ms; a.n++; }
    if (m.model) a.model = m.model;
    if (m.provider) a.provider = m.provider;
    return a;
  }, { p:0, c:0, tot:0, msSum:0, n:0, model:'', provider:'' });
  const avgSec = stat.n ? (stat.msSum / stat.n / 1000).toFixed(1) : null;

  // Regex sama persis dgn CHART_INTENT_RE di server.js (aiCache/server) — jaga
  // konsisten agar keputusan followup di client match keputusan chart di server.
  const CHART_INTENT_RE = /\b(chart|grafik|graph|visuali[sz]e|visualisasi|visualisasikan|visualkan|plot|diagram|pie|donut|radar|bar\s?chart)\b/i;

  // Kirim pertanyaan (dipakai send biasa & kirim-ulang setelah edit).
  const ask = async (q) => {
    setErr(''); setBusy(true);
    // Follow-up HANYA utk permintaan chart/visualisasi ("buatkan pie chart" dst).
    // PENTING: jangan aktifkan followup utk pertanyaan data biasa ("cost JB 2025")
    // — jalur followup TIDAK menjalankan SQL baru, cuma menganalisis tabel LAMA.
    // Pernah salah: sempat mengaktifkan followup utk SEMUA pertanyaan susulan,
    // akibatnya filter tahun/BU baru diabaikan & jawaban mengutip angka basi
    // dari query sebelumnya (lihat insiden "cost for JB 2025" tetap $1,991.53).
    const lastRows = CHART_INTENT_RE.test(q) ? (() => {
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].role === 'assistant' && Array.isArray(msgs[i].rows) && msgs[i].rows.length) return msgs[i].rows;
      }
      return null;
    })() : null;
    setMsgs(m => [...m, { role: 'user', content: q }]);
    const t0 = Date.now();
    try {
      const res = await fetch(`${API}/api/share/${encodeURIComponent(shareKey)}/ai`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: q, session_id: sid, lang: id ? 'id' : 'en',
          ...(lastRows ? { mode: 'followup', prev_rows: lastRows.slice(0, 100) } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      if (data.session_id) setSid(data.session_id);
      setMsgs(m => [...m, {
        role: 'assistant', content: data.answer || '',
        rows: Array.isArray(data.rows) ? data.rows : null,
        sql: data.sql || null,
        usage: data.usage || null, model: data.model || null, provider: data.provider || null,
        ms: Date.now() - t0, _animate: true,
      }]);
    } catch (e) {
      setErr(e.message || t('shared.failedGetAnswer'));
    } finally {
      setBusy(false);
      // Sesi baru belum ada di daftar sampai pesan pertamanya tersimpan, jadi
      // daftar disegarkan SESUDAH jawaban masuk. Hanya saat sidebar terlihat —
      // di mode floating tidak ada yang membacanya.
      if (showHistory) loadSessions(sessQDeb);
    }
  };
  const send = () => {
    const q = input.trim();
    if (!q || busy) return;
    if (q.startsWith('/')) { handleSlash(q); return; }   // command lokal, tak dikirim ke server
    setInput('');
    ask(q);
  };
  // Edit pesan user: hapus HANYA pesan itu + jawabannya, lalu kirim ulang teks baru.
  const startEdit = (i) => { setEditIdx(i); setEditText(msgs[i]?.content || ''); };
  const submitEdit = () => {
    const q = editText.trim();
    if (!q || busy) return;
    const i = editIdx;
    setEditIdx(-1); setEditText('');
    setMsgs(m => m.filter((_, mi) => mi !== i && !(mi === i + 1 && m[i + 1]?.role === 'assistant')));
    ask(q);
  };
  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  // ── Drag (dari header) & Resize (dari pojok kiri-bawah) ─────────────────────
  // Pointer events (bukan mouse) agar jalan di touch. Clamp ke viewport supaya
  // panel tak pernah hilang di luar layar. Nonaktif saat max/standalone.
  // Handler disimpan di ref agar add/removeEventListener selalu pakai referensi
  // yang sama (fungsi inline akan beda tiap render → gagal remove).
  const MIN_W = 320, MIN_H = 360;
  const moveHandlerRef = useRef(null);
  const beginGesture = (kind) => (e) => {
    if (max || standalone) return;
    e.preventDefault();
    const r = panelRef.current?.getBoundingClientRect();
    if (!r) return;
    const g = { kind, startX: e.clientX, startY: e.clientY, left: r.left, top: r.top, width: r.width, height: r.height };
    const onMove = (ev) => {
      const dx = ev.clientX - g.startX, dy = ev.clientY - g.startY;
      const vw = window.innerWidth, vh = window.innerHeight;
      if (g.kind === 'drag') {
        const left = Math.min(Math.max(0, g.left + dx), vw - g.width);
        const top  = Math.min(Math.max(0, g.top + dy),  vh - 40);
        setFloatBox({ left, top, width: g.width, height: g.height });
      } else {
        const width  = Math.min(Math.max(MIN_W, g.width - dx), vw - g.left);
        const left   = g.left + (g.width - width);
        const height = Math.min(Math.max(MIN_H, g.height + dy), vh - g.top);
        setFloatBox({ left, top: g.top, width, height });
      }
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      moveHandlerRef.current = null;
    };
    moveHandlerRef.current = onMove;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  useEffect(() => () => { if (moveHandlerRef.current) window.removeEventListener('pointermove', moveHandlerRef.current); }, []);

  // ── SLASH COMMANDS — diproses LOKAL, tidak dikirim ke server (spt AIChat asli) ─
  // Didukung di share: /clear|/new|/reset, /help, /sql. (/examples di-skip:
  // share tak punya daftar quick-prompt.)
  const handleSlash = (userText) => {
    const cmd = userText.slice(1).trim().toLowerCase().split(/\s+/)[0];
    const note = (content) => setMsgs(prev => [...prev, { role: 'system-note', content }]);
    if (cmd === 'clear' || cmd === 'new' || cmd === 'reset') {
      setMsgs([]); setSid(null); setErr(''); setInput('');
      return true;
    }
    if (cmd === 'help') {
      setInput('');
      note(t('shared.availableCommandsHelpShowHelp'));
      return true;
    }
    if (cmd === 'sql') {
      setInput('');
      const lastSql = [...msgs].reverse().find(m => m.role === 'assistant' && m.sql)?.sql;
      note(lastSql
        ? t('shared.sqlLastAnswer') + '\n```sql\n' + lastSql + '\n```'
        : t('shared.noSqlYetAskData'));
      return true;
    }
    setInput('');
    note(t('shared.unknownCommandCmdTypeHelp', {
      cmd
    }));
    return true;
  };

  if (!open) {
    // Portal ke document.body: kebal thd ancestor ber-transform/overflow yang
    // merusak position:fixed, dan zIndex tinggi agar tidak tertutup elemen
    // dashboard (sticky header/tab z-30/z-50) — akar masalah tombol tak
    // terlihat di browser mobile. safe-area utk home-bar iOS/Android gesture.
    return createPortal(
      <button onClick={() => setOpen(true)}
        style={{ position:'fixed', right:16, bottom:'calc(16px + env(safe-area-inset-bottom, 0px))', zIndex:1000,
                 padding:'12px 18px', borderRadius:999,
                 border:'none', cursor:'pointer', background:'rgb(var(--brand-600))', color:'#fff', fontWeight:700, fontSize:13.5,
                 boxShadow:'0 4px 14px rgba(37,99,235,0.35)' }}>
        {t('shared.askAi')}
      </button>,
      document.body
    );
  }

  const panelStyle = max
    ? { position:'fixed', inset:0, zIndex:1001, width:'100vw', height:'100vh', borderRadius:0 }
    : standalone
      ? { position:'relative', width:'min(1040px, calc(100vw - 40px))', height:'min(680px, calc(100vh - 140px))', margin:'28px auto', borderRadius:16 }
      : floatBox
        ? { position:'fixed', left:floatBox.left, top:floatBox.top, width:floatBox.width, height:floatBox.height, zIndex:1000, borderRadius:16 }
        : { position:'fixed', right:16, bottom:'calc(16px + env(safe-area-inset-bottom, 0px))', zIndex:1000,
            width:'min(460px, calc(100vw - 32px))', height:'min(600px, calc(100vh - 90px))', borderRadius:16 };
  const canMove = !max && !standalone;   // drag/resize hanya di mode floating

  // String sidebar ditaruh LOKAL, bukan di translations.js. Alasannya sama
  // dengan AIHistorySidebar.js: gate build check-i18n.js menuntut paritas tiga
  // bahasa untuk tiap key, dan halaman share hanya punya pemilih ID/EN.
  const L = {
    id: { history:'Riwayat', newChat:'Chat baru', empty:'Belum ada riwayat.',
          loading:'Memuat…', failed:'Gagal memuat riwayat.', msgs:'pesan', untitled:'(tanpa judul)',
          search:'Cari riwayat…', noMatch:'Tidak ada yang cocok.', clear:'Hapus pencarian',
          shown:(n) => `${n} sesi ditampilkan`,
          capped:(n) => `${n} sesi terbaru — sesi lebih lama tidak ditampilkan`,
          shared:'Riwayat dibagikan ke semua pemegang link ini.',
          rename:'Ganti judul', save:'Simpan', cancelEdit:'Batal',
          renameHint:'Kosongkan untuk kembali ke judul otomatis.' },
    en: { history:'History', newChat:'New chat', empty:'No history yet.',
          loading:'Loading…', failed:'Failed to load history.', msgs:'messages', untitled:'(untitled)',
          search:'Search history…', noMatch:'No matches.', clear:'Clear search',
          shown:(n) => `${n} sessions shown`,
          capped:(n) => `${n} most recent sessions — older ones are not shown`,
          shared:'History is shared with everyone holding this link.',
          rename:'Rename', save:'Save', cancelEdit:'Cancel',
          renameHint:'Leave empty to restore the automatic title.' },
  }[id ? 'id' : 'en'];

  // Waktu di DB adalah UTC (datetime('now')), tanpa penanda zona. Tanpa 'Z'
  // browser menganggapnya waktu lokal dan label jamnya meleset sebesar offset
  // zona pembaca (+8 jam di WITA).
  const fmtWhen = (s) => {
    if (!s) return '';
    const d = new Date(String(s).replace(' ', 'T') + 'Z');
    if (isNaN(d)) return '';
    return d.toLocaleString(id ? 'id-ID' : 'en-GB',
      { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' });
  };

  const historySidebar = (
    <aside style={{ width:230, flexShrink:0, borderRight:'1px solid #eef2f7', background:'#fafbfc',
                    display:'flex', flexDirection:'column', minHeight:0 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                    padding:'10px 12px', borderBottom:'1px solid #eef2f7' }}>
        <span style={{ fontWeight:700, fontSize:12, color:'#0f172a' }}>
          {L.history}{sessBusy && sessions.length > 0 ? ' ·' : ''}
        </span>
        <button onClick={newChat} disabled={busy} title={L.newChat}
          style={{ border:'1px solid rgb(var(--brand-100))', background:'rgb(var(--brand-50))', color:'rgb(var(--brand-600))',
                   borderRadius:8, padding:'3px 9px', fontSize:11.5, fontWeight:700,
                   cursor: busy ? 'not-allowed' : 'pointer' }}>
          + {L.newChat}
        </button>
      </div>
      <div style={{ position:'relative', padding:'8px 10px 6px', borderBottom:'1px solid #eef2f7' }}>
        <input value={sessQ} onChange={(e) => setSessQ(e.target.value)}
          placeholder={L.search} aria-label={L.search}
          style={{ width:'100%', boxSizing:'border-box', border:'1px solid #e2e8f0', borderRadius:8,
                   padding:'6px 26px 6px 9px', fontSize:11.5, fontFamily:'inherit', outline:'none',
                   background:'#fff', color:'#0f172a' }} />
        {sessQ && (
          <button onClick={() => setSessQ('')} title={L.clear}
            style={{ position:'absolute', right:16, top:'50%', transform:'translateY(-40%)',
                     border:'none', background:'transparent', cursor:'pointer',
                     color:'#94a3b8', fontSize:14, lineHeight:1, padding:2 }}>×</button>
        )}
      </div>
      <div style={{ flex:1, overflowY:'auto', padding:'6px' }}>
        {/* Spinner besar hanya saat daftar masih KOSONG. Saat menyegarkan,
            daftar lama tetap terpasang agar posisi scroll tidak runtuh. */}
        {sessBusy && sessions.length === 0 && (
          <div style={{ color:'#94a3b8', fontSize:12, textAlign:'center', margin:'1rem 0' }}>{L.loading}</div>
        )}
        {!sessBusy && sessErr && (
          <div style={{ color:'#dc2626', fontSize:11.5, padding:'8px 6px' }}>{L.failed}</div>
        )}
        {!sessBusy && !sessErr && sessions.length === 0 && (
          <div style={{ color:'#94a3b8', fontSize:12, textAlign:'center', margin:'1rem 0' }}>
            {sessQDeb ? L.noMatch : L.empty}
          </div>
        )}
        {sessions.map(s => {
          const active = s.session_id === sid;
          if (s.session_id === renameId) {
            return (
              <div key={s.session_id} onClick={(e) => e.stopPropagation()}
                style={{ border:'1px solid #bfdbfe', background:'#fff', borderRadius:8,
                         padding:'7px 8px', marginBottom:4 }}>
                <input value={renameText} maxLength={TITLE_MAX} autoFocus
                  onChange={(e) => setRenameText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); commitRename(s.session_id, e); }
                    if (e.key === 'Escape') cancelRename();
                  }}
                  style={{ width:'100%', boxSizing:'border-box', border:'1px solid #cbd5e1',
                           borderRadius:6, padding:'5px 7px', fontSize:11.5, fontFamily:'inherit',
                           outline:'none' }} />
                <div style={{ fontSize:9.5, color:'#94a3b8', marginTop:3 }}>{L.renameHint}</div>
                <div style={{ display:'flex', gap:6, justifyContent:'flex-end', marginTop:5 }}>
                  <button onClick={cancelRename} disabled={renameBusy}
                    style={{ border:'1px solid #e2e8f0', background:'#fff', color:'#64748b',
                             borderRadius:6, padding:'3px 9px', fontSize:11, cursor:'pointer' }}>
                    {L.cancelEdit}
                  </button>
                  <button onClick={(e) => commitRename(s.session_id, e)} disabled={renameBusy}
                    style={{ border:'none', background:'rgb(var(--brand-600))', color:'#fff', borderRadius:6,
                             padding:'3px 11px', fontSize:11, fontWeight:700,
                             cursor: renameBusy ? 'not-allowed' : 'pointer' }}>
                    {L.save}
                  </button>
                </div>
              </div>
            );
          }
          return (
            <div key={s.session_id}
              // Guard modifier wajib berpasangan dengan <a> di dalam: tanpa ini
              // satu Ctrl+Click membuka tab baru DAN mengganti sesi di tab ini.
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
                openSession(s.session_id);
              }}
              style={{ display:'flex', alignItems:'flex-start', gap:4, marginBottom:4,
                       border:'1px solid ' + (active ? '#bfdbfe' : 'transparent'),
                       background: active ? 'rgb(var(--brand-50))' : 'transparent',
                       borderRadius:8, padding:'7px 6px 7px 9px',
                       cursor: (busy || loadingSid) ? 'not-allowed' : 'pointer' }}>
              <div style={{ flex:1, minWidth:0 }}>
                {/* Dua baris, sama seperti AIHistorySidebar internal. Satu baris
                    ber-ellipsis memotong terlalu dini pada judul otomatis yang
                    diambil dari pertanyaan pertama — kalimatnya panjang dan
                    bagian yang membedakan justru sering ada di belakang.
                    `wordBreak` mencegah URL atau path panjang meluber. */}
                {/* Anchor sungguhan, bukan div: hanya <a href> yang membuat
                    "Open link in new tab" muncul di menu klik-kanan browser.
                    Klik kiri biasa di-preventDefault dan diteruskan (bubbling)
                    ke onClick baris, jadi perilaku lama tidak berubah. */}
                <a href={`/share/${encodeURIComponent(shareKey)}?session=${encodeURIComponent(s.session_id)}`}
                  draggable={false}
                  onClick={(e) => {
                    // Modifier dilepas ke browser — preventDefault akan
                    // membatalkan Ctrl+Click / buka tab baru.
                    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
                    e.preventDefault();
                  }}
                  style={{ fontSize:12, color:'#0f172a', fontWeight: active ? 700 : 500,
                           display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical',
                           overflow:'hidden', lineHeight:1.35, wordBreak:'break-word',
                           textDecoration:'none' }}>
                  {s.title || L.untitled}
                </a>
                <div style={{ fontSize:10.5, color:'#94a3b8', marginTop:2 }}>
                  {fmtWhen(s.last_at)} · {s.msg_count} {L.msgs}
                  {loadingSid === s.session_id ? ' · ' + L.loading : ''}
                </div>
              </div>
              <button onClick={(e) => startRename(s, e)} title={L.rename}
                style={{ border:'none', background:'transparent', cursor:'pointer',
                         color:'#94a3b8', fontSize:12, lineHeight:1, padding:'2px 3px' }}>✎</button>
            </div>
          );
        })}
      </div>

      {/* Keterangan batas. Dibedakan: daftar TERPOTONG (jumlah = batas) vs
          sekadar sedikit sesi — kalau disamakan, pengguna yang riwayatnya
          memang pendek akan mengira ada sesi yang disembunyikan. */}
      {sessions.length > 0 && (
        <div style={{ borderTop:'1px solid #eef2f7', padding:'6px 10px', background:'#fafbfc',
                      fontSize:10, lineHeight:1.45, color:'#94a3b8' }}>
          <div>
            {sessLimit && sessions.length >= sessLimit ? L.capped(sessLimit) : L.shown(sessions.length)}
          </div>
          <div style={{ marginTop:2 }}>{L.shared}</div>
        </div>
      )}
    </aside>
  );

  const panel = (
    <div ref={panelRef} style={{ ...panelStyle, background:'#fff', border:'1px solid #e2e8f0', boxShadow:'0 10px 40px rgba(0,0,0,0.18)', display:'flex', flexDirection:'column' }}>
      <div onPointerDown={canMove ? beginGesture('drag') : undefined}
        style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 14px', borderBottom:'1px solid #eef2f7', cursor: canMove ? 'move' : 'default', touchAction:'none', userSelect:'none' }}>
        <span style={{ fontWeight:700, fontSize:13.5, color:'#0f172a' }}>{t('shared.aiAssistant')}</span>
        <div style={{ display:'flex', gap:6 }}>
          <button onClick={() => setMax(v => !v)} title={max ? t('shared.minimize') : t('shared.maximize')}
            style={{ border:'none', background:'transparent', cursor:'pointer', color:'#64748b', fontSize:15, lineHeight:1 }}>
            {max ? '🗗' : '⛶'}
          </button>
          {!standalone && (
            <button onClick={() => setOpen(false)} style={{ border:'none', background:'transparent', cursor:'pointer', color:'#94a3b8', fontSize:18, lineHeight:1 }}>×</button>
          )}
        </div>
      </div>

      {/* Badan panel: sidebar riwayat (hanya maximize) + kolom chat.
          minHeight:0 WAJIB — tanpa itu anak ber-overflow di dalam flex tidak
          pernah menyusut dan scroll internalnya mati. */}
      <div style={{ flex:1, display:'flex', minHeight:0 }}>
      {showHistory && historySidebar}
      <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0, minHeight:0 }}>

      {/* Stats bar: model + token + waktu (fitur utama) */}
      {(stat.tot > 0 || stat.model) && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:'4px 12px', padding:'6px 14px', borderBottom:'1px solid #f1f5f9', fontSize:11.5, color:'#64748b', background:'#fafbfc' }}>
          {stat.model && <span title={t('shared.aiModelUsed')}>🧠 {stat.provider ? `${stat.provider} · ` : ''}{stat.model}</span>}
          {stat.tot > 0 && <span title={t('shared.tokensPromptCompletionTotal')}>⚡ {stat.p}↑ {stat.c}↓ = {stat.tot} tokens</span>}
          {avgSec && <span title={t('shared.avgResponseTime')}>⏱ {avgSec}s</span>}
        </div>
      )}

      <div style={{ flex:1, overflowY:'auto', display:'flex', flexDirection:'column', gap:10, padding:'12px 14px' }}>
        {msgs.length === 0 && (
          <div style={{ color:'#94a3b8', fontSize:13, textAlign:'center', margin:'1.5rem 0' }}>
            {t('shared.askAboutDashboardSData')}
            {(SHARE_QUICK_PROMPTS[shareKey]?.[id ? 'id' : 'en'] || []).length > 0 && (
              <div style={{ display:'flex', flexWrap:'wrap', gap:8, justifyContent:'center', marginTop:16 }}>
                {SHARE_QUICK_PROMPTS[shareKey][id ? 'id' : 'en'].map(q => (
                  <button key={q} onClick={() => !busy && ask(q)} disabled={busy}
                    style={{ border:'1px solid rgb(var(--brand-100))', background:'rgb(var(--brand-50))',
                             color:'rgb(var(--brand-600))', borderRadius:999, padding:'7px 13px',
                             fontSize:12.5, fontWeight:600, cursor: busy ? 'not-allowed' : 'pointer',
                             fontFamily:'inherit', textAlign:'left' }}>
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {msgs.map((m, i) => (
          m.role === 'system-note' ? (
            <div key={i} style={{ alignSelf:'flex-start', maxWidth:'92%', background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:12, padding:'8px 12px', fontSize:12.5, color:'#475569' }}>
              <MarkdownView source={m.content} />
            </div>
          ) :
          m.role === 'user' ? (
            editIdx === i ? (
              <div key={i} style={{ alignSelf:'flex-end', width:'92%' }}>
                <textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={2} autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitEdit(); } if (e.key === 'Escape') { setEditIdx(-1); } }}
                  style={{ width:'100%', resize:'vertical', border:'1px solid rgb(var(--brand-600))', borderRadius:10, padding:'8px 10px', fontSize:13, fontFamily:'inherit', outline:'none' }} />
                <div style={{ display:'flex', gap:6, justifyContent:'flex-end', marginTop:4 }}>
                  <button onClick={() => setEditIdx(-1)} style={{ padding:'5px 12px', borderRadius:8, border:'1px solid #e2e8f0', background:'#fff', color:'#64748b', fontSize:12, cursor:'pointer' }}>{t('shared.cancel')}</button>
                  <button onClick={submitEdit} disabled={!editText.trim() || busy} style={{ padding:'5px 12px', borderRadius:8, border:'none', background:'rgb(var(--brand-600))', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>{t('shared.resend')}</button>
                </div>
              </div>
            ) : (
              <div key={i} style={{ alignSelf:'flex-end', maxWidth:'88%', display:'flex', alignItems:'center', gap:6 }}>
                <button onClick={() => startEdit(i)} title={t('shared.editResend')}
                  style={{ border:'none', background:'transparent', cursor:'pointer', color:'#94a3b8', fontSize:13, lineHeight:1, padding:2 }}>✎</button>
                <div style={{ background:'rgb(var(--brand-600))', color:'#fff', borderRadius:12, padding:'9px 12px', fontSize:13, lineHeight:1.5, wordBreak:'break-word' }}>
                  {m.content}
                </div>
              </div>
            )
          ) : (
            <div key={i} style={{ alignSelf:'flex-start', maxWidth:'100%', width: m.rows ? '100%' : 'auto' }}>
              <AssistantBubble msg={m} id={id}
                onDoneAnimate={m._animate ? () => setMsgs(prev => prev.map((x, xi) => xi === i ? { ...x, _animate: false } : x)) : undefined} />
              {/* Keterangan per pesan: token (prompt↑ completion↓ = total) + waktu respons. */}
              {(m.usage?.total_tokens > 0 || m.ms > 0) && (
                <div style={{ display:'flex', gap:10, marginTop:3, paddingLeft:2, fontSize:10.5, color:'#94a3b8' }}>
                  {m.usage?.total_tokens > 0 && (
                    <span title={t('shared.tokensPromptCompletionTotal')}>
                      ⚡ {m.usage.prompt_tokens || 0}↑ {m.usage.completion_tokens || 0}↓ = {m.usage.total_tokens} tok
                    </span>
                  )}
                  {m.ms > 0 && <span title={t('shared.responseTime')}>⏱ {(m.ms / 1000).toFixed(1)}s</span>}
                </div>
              )}
            </div>
          )
        ))}
        {busy && <div style={{ alignSelf:'flex-start', color:'#94a3b8', fontSize:13 }}>{t('shared.aiTyping')}</div>}
      </div>

      {err && <div style={{ background:'#fff1f2', color:'#dc2626', border:'1px solid #fecdd3', borderRadius:8, padding:'7px 10px', fontSize:12, margin:'0 12px 8px' }}>{err}</div>}

      <div style={{ display:'flex', gap:8, alignItems:'flex-end', padding:'10px 12px', borderTop:'1px solid #eef2f7' }}>
        <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey} rows={1}
          placeholder={t('shared.typeQuestionHelpCommands')}
          style={{ flex:1, resize:'none', border:'1px solid #cbd5e1', borderRadius:10, padding:'9px 11px', fontSize:13, fontFamily:'inherit', outline:'none', maxHeight:120 }} />
        <button onClick={send} disabled={busy || !input.trim()}
          style={{ padding:'9px 16px', borderRadius:10, border:'none', fontSize:13, fontWeight:700,
                   cursor: busy || !input.trim() ? 'not-allowed' : 'pointer',
                   background: busy || !input.trim() ? '#cbd5e1' : 'rgb(var(--brand-600))', color:'#fff' }}>
          {t('shared.send')}
        </button>
      </div>

      </div>{/* /kolom chat */}
      </div>{/* /badan panel */}

      {/* Resize handle (pojok kiri-bawah) — hanya di mode floating */}
      {canMove && (
        <div onPointerDown={beginGesture('resize')} title={t('shared.resize')}
          style={{ position:'absolute', left:0, bottom:0, width:18, height:18, cursor:'nesw-resize', touchAction:'none',
                   background:'linear-gradient(45deg, transparent 50%, #cbd5e1 50%, #cbd5e1 60%, transparent 60%, transparent 72%, #cbd5e1 72%, #cbd5e1 82%, transparent 82%)',
                   borderBottomLeftRadius:16 }} />
      )}
    </div>
  );
  // standalone dirender inline (bagian layout halaman); mode floating/max lewat
  // portal ke body — konsisten dgn tombol pembuka (fix mobile).
  return standalone ? panel : createPortal(panel, document.body);
}

export default function SharedDashboard() {
  const { key }              = useParams();
  const {
    lang,
    setLang,
    t
  }    = useLang();
  const id                   = lang !== 'en';

  const [meta,   setMeta]    = useState(null);        // { key, label, params? }
  const [state,  setState]   = useState('loading');   // loading | ready | error
  const [errMsg, setErrMsg]  = useState('');

  const isNote = key === 'note';
  const isAi   = key === 'ai-workspace';
  const View   = (isNote || isAi) ? null : SHARE_VIEWS[key];
  // Key tanpa komponen visual (mis. 'm365') masih valid ASAL server konfirmasi
  // AI tersedia untuk key ini — dirender chat-only (tanpa tabel/dashboard).
  // Keputusan final menunggu /meta di bawah (butuh data.ai.available).
  const isChatOnly = !isNote && !isAi && !View;

  // Loader data yang disuntik ke dashboard — pakai token share (tanpa JWT).
  const dataLoader = useCallback(async (years = []) => {
    const yq  = years.length ? `?years=${years.join(',')}` : '';
    const res = await fetch(`${API}/api/share/${encodeURIComponent(key)}/data${yq}`, { credentials: 'include' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  }, [key]);

  // Fetcher generik utk komponen multi-endpoint (M365Dashboard, EmployeeOvertime):
  // path asli "/api/m365/summary?x=y" -> "/api/share/m365/data?resource=summary&x=y".
  // Dipakai sbg pengganti authGet/useApi internal komponen — perilaku (filter,
  // pagination, tab) tetap identik, cuma transport-nya beda (cookie vs JWT).
  const makeResourceFetcher = useCallback((apiPrefix) => async (path) => {
    const [base, qs] = path.split('?');
    const resource = base.replace(apiPrefix, '');
    const params = new URLSearchParams(qs || '');
    params.set('resource', resource);
    const res = await fetch(`${API}/api/share/${encodeURIComponent(key)}/data?${params.toString()}`, { credentials: 'include' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  }, [key]);
  const m365Fetcher     = useMemo(() => makeResourceFetcher('/api/m365/'), [makeResourceFetcher]);
  const overtimeFetcher = useMemo(() => makeResourceFetcher('/api/overtime/'), [makeResourceFetcher]);

  // Props spesifik per key — komponen multi-endpoint pakai `dataFetcher`,
  // komponen snapshot-tunggal (aws-cost/billing/solman) pakai `dataLoader`+`token`.
  const VIEW_PROPS = {
    'm365':              { dataFetcher: m365Fetcher },
    'employee-overtime': { dataFetcher: overtimeFetcher },
  };
  const viewProps = VIEW_PROPS[key] || { dataLoader, token: null };

  // Validasi awal + ambil judul (sekaligus memastikan token sah).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res  = await fetch(`${API}/api/share/${encodeURIComponent(key)}/meta`, { credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        if (!alive) return;
        // Key chat-only (tanpa View) HANYA valid bila server konfirmasi source
        // AI terdaftar untuk key ini. Tanpa View DAN tanpa AI = tak bisa dibagikan.
        if (isChatOnly && !data?.ai?.available) {
          setErrMsg(t('shared.dashboardNotAvailableSharing'));
          setState('error'); return;
        }
        setMeta(data); setState('ready');
      } catch (e) {
        if (!alive) return;
        setErrMsg(e.message || (t('shared.invalidLink')));
        setState('error');
      }
    })();
    return () => { alive = false; };
  }, [key, View, isNote, isAi, isChatOnly, id]);

  // ── Error / link tidak valid ──
  if (state === 'error') {
    return (
      <div style={{ minHeight:'100vh', background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'center', padding:24, fontFamily:"'DM Sans',sans-serif" }}>
        <div style={{ textAlign:'center', maxWidth:420 }}>
          <div style={{ width:64, height:64, borderRadius:16, background:'#fff1f2', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px', fontSize:30 }}>🔒</div>
          <h1 style={{ fontFamily:"'Sora',sans-serif", fontSize:20, fontWeight:700, color:'#1e293b', margin:'0 0 8px' }}>
            {t('shared.linkUnavailable')}
          </h1>
          <p style={{ fontSize:14, color:'#64748b', lineHeight:1.6, margin:0 }}>
            {errMsg || (t('shared.linkInvalidHasExpired'))}
          </p>
        </div>
      </div>
    );
  }

  // ── Loading ──
  if (state === 'loading') {
    return (
      <div style={{ minHeight:'100vh', background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div style={{ width:32, height:32, border:'3px solid rgb(var(--brand-600))', borderTopColor:'transparent', borderRadius:'50%', animation:'shspin 0.8s linear infinite' }} />
        <style>{`@keyframes shspin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  // ── Ready: shell publik (tanpa sidebar/login) + dashboard read-only ──
  return (
    <div style={{ minHeight:'100vh', background:'#f5f5f0' }}>
      <header style={{ height:56, background:'#fff', borderBottom:'1px solid #e2e8f0', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 20px', position:'sticky', top:0, zIndex:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:32, height:32, borderRadius:8, background:'rgb(var(--brand-600))', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:700, fontSize:14 }}>P</div>
          <div>
            <div style={{ fontFamily:"'Sora',sans-serif", fontWeight:700, fontSize:14, color:'#1e293b', lineHeight:1.1 }}>{meta?.label || key}</div>
            <div style={{ fontSize:11, color:'#94a3b8' }}>{t('shared.publicViewReadOnly')}</div>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ display:'flex', gap:3, background:'#f1f5f9', borderRadius:999, padding:3 }}>
            {['id', 'en'].map(code => (
              <button
                key={code}
                onClick={() => setLang(code)}
                style={{
                  padding:'3px 10px', borderRadius:999, border:'none', cursor:'pointer',
                  fontSize:11, fontWeight:700, letterSpacing:0.3,
                  background: lang === code ? '#fff' : 'transparent',
                  color:      lang === code ? 'rgb(var(--brand-600))' : '#64748b',
                  boxShadow:  lang === code ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>
          <span style={{ fontSize:11, fontWeight:600, color:'rgb(var(--brand-600))', background:'#e0f2fe', padding:'4px 10px', borderRadius:999 }}>
            {t('shared.readOnly')}
          </span>
        </div>
      </header>
      {isAi
        ? <AiWorkspaceView id={id} />
        : isNote
          ? <NoteView noteKey={key} id={id} />
          : View
            ? <>
                <View {...viewProps} />
                {meta?.ai?.enabled && <ShareSqlAiPanel shareKey={key} id={id} />}
              </>
            : meta?.ai?.enabled
              ? <ShareSqlAiPanel shareKey={key} id={id} standalone />
              : (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'60vh', padding:24 }}>
                  <p style={{ color:'#64748b', fontSize:14, textAlign:'center', maxWidth:360 }}>
                    {t('shared.aiHasNotBeenEnabled')}
                  </p>
                </div>
              )}
    </div>
  );
}
